import { Vector3 } from "three";
import { cylinderDistance, type CylinderSurface } from "./cylinder-surface";

const MAX_SAMPLES = 8192;
export const CYLINDER_DEVIATION_BINS = 21;

/** Bounded reservoir of observed world-space cylinder points. */
export class CylinderDeviationSamples {
  private readonly points: Float64Array;
  private seen = 0;

  /** @param capacity Maximum retained observations. */
  constructor(private readonly capacity = MAX_SAMPLES) {
    this.points = new Float64Array(capacity * 3);
  }

  /** @param point Observed point to copy into the reservoir. */
  add(point: Vector3): void {
    if (
      !Number.isFinite(point.x) ||
      !Number.isFinite(point.y) ||
      !Number.isFinite(point.z)
    ) {
      return;
    }
    const index =
      this.seen < this.capacity
        ? this.seen
        : Math.floor(Math.random() * (this.seen + 1));
    this.seen++;
    if (index < this.capacity) point.toArray(this.points, index * 3);
  }

  /**
   * @param accepts Current selection membership predicate.
   * @returns Independently owned coordinates of retained selected observations.
   */
  snapshot(accepts: (point: Vector3) => boolean): Float64Array {
    const result: number[] = [];
    const point = new Vector3();
    for (let index = 0; index < Math.min(this.seen, this.capacity); index++) {
      point.fromArray(this.points, index * 3);
      if (accepts(point)) result.push(point.x, point.y, point.z);
    }
    return new Float64Array(result);
  }
}

/**
 * @param samples Packed observed world-space coordinates.
 * @param cylinder Current fitted model.
 * @param tolerance Signed display range extends from minus to plus tolerance.
 * @returns Sample counts in equally spaced signed-deviation bins.
 */
export function cylinderDeviationHistogram(
  samples: Float64Array | undefined,
  cylinder: CylinderSurface,
  tolerance: number,
): number[] {
  const bins = new Array<number>(CYLINDER_DEVIATION_BINS).fill(0);
  if (!samples || !Number.isFinite(tolerance) || tolerance <= 0) return bins;
  const point = new Vector3();
  for (let index = 0; index + 2 < samples.length; index += 3) {
    const distance = cylinderDistance(
      cylinder,
      point.fromArray(samples, index),
    );
    if (!Number.isFinite(distance) || Math.abs(distance) > tolerance) continue;
    const bin = Math.min(
      bins.length - 1,
      Math.floor(((distance / tolerance + 1) / 2) * bins.length),
    );
    bins[bin]++;
  }
  return bins;
}
